/**
 * English copy of the five lead-scoring steps of the setup guide, including the
 * per-screen headers of the steps that walk several screens. Split out of
 * enOnboardingTour.ts to keep both files under the 500-line cap.
 * Keys are addressed as onboardingTour.steps.<...>.
 */
export const enOnboardingTourScoringSteps = {
	scoringIntro: {
		railLabel: "What is Lead Scoring?",
		title: "Your leads, sorted for you",
		desc: "Signalist sorts every new lead into your customer groups and grades how well it fits — so your team always knows who to contact first.",
		railNote:
			"Before your leads come in: <b>Lead Scoring</b>. It does two things — it puts every lead into one of <b>your customer groups</b>, and it grades how well the lead fits. I've already built a draft from your website; you just review it. <b>About 4 minutes.</b>",
		railNoteInvited:
			"Your team already set up Lead Scoring. Here's what it does — every lead lands in one of your customer groups and gets graded on how well it fits.",
	},
	scoringSegments: {
		railLabel: "Your customer groups",
		// PREC-3896: the headline never said how many groups there are, nor that the screen wants a
		// verdict on each — Robert watched a user read it as a statement rather than a task. The
		// count comes from the step (`titleCount`), so the plural forms are what i18next selects on.
		title_one: "Signalist has identified 1 company segment for you",
		title_other: "Signalist has identified {{count}} company segments for you",
		desc: "Go through them one at a time and tell us whether each one fits. Click any text to correct it.",
		railNote:
			"These are your <b>company segments</b> — the groups every lead gets sorted into. They also drive your Knowledge Base and your filters later, so give each one a quick look: does the <b>description</b> match how you'd describe these customers? And don't worry — you can add, edit or delete your segments at any time later in the platform.",
		screens: {
			customerGroups: {
				stepper: "Your customer groups",
				title_one: "Signalist has identified 1 company segment for you",
				title_other: "Signalist has identified {{count}} company segments for you",
				desc: "Go through them one at a time and tell us whether each one fits. Click any text to correct it.",
				railNote:
					"These are your <b>company segments</b> — the groups every lead gets sorted into. They also drive your Knowledge Base and your filters later, so give each one a quick look: does the <b>description</b> match how you'd describe these customers? And don't worry — you can add, edit or delete your segments at any time later in the platform.",
			},
			partners: {
				stepper: "Partners",
				title: "Do you also work with partners?",
				desc: "Companies that refer or resell rather than buy from you. If that's part of your model, set them up as their own groups.",
				railNote:
					"Quick question: do you have <b>referral or reseller partners</b>? We can't read partnerships off your website, so this one starts empty — if you say yes, describe each partner group like you did your customers, and we'll qualify partners <b>separately</b>, never mixed into your buyer scoring. Partner groups too can be changed at any time later.",
			},
			extras: {
				stepper: "Investors & exclusions",
				title: "Investors, competitors & who to skip",
				desc: "Three optional settings. Switch on or open what applies to you — leave the rest as it is.",
				railNote:
					"Three optional extras: track <b>investors</b> as their own group, describe your <b>competitors</b> so we can tell them apart from prospects, and tell us who to <b>never contact</b> — those get filtered out completely.",
			},
		},
	},
	scoringRoles: {
		railLabel: "Your buyer roles",
		title_one: "Signalist has identified 1 buyer role for you",
		title_other: "Signalist has identified {{count}} buyer roles for you",
		desc: "The people inside those companies. Go through them one role at a time and tell us whether each one fits — click any text to correct it.",
		railNote:
			"Now the <b>people</b> — your persona segments. Each one has a description and the job titles we match on (plus similar titles, automatically). The <b>“Relevant for”</b> chips control which customer groups a role applies to. Like your segments, roles stay editable in the platform at any time.",
	},
	scoringGrading: {
		railLabel: "What makes a Prio 1?",
		title: "What makes a lead Prio 1 for you?",
		desc: "Each criterion gives a lead points — or disqualifies it. The points add up to the Prio grade.",
		railNote:
			"Now the <b>grading</b>. Every active criterion scores a lead <b>+1 to +3 points</b> — or <b>disqualifies</b> it — and the total becomes Prio 1, 2 or 3. The <b>Strategic Fit</b> agent judges what hard numbers can't, described in your own words.",
		screens: {
			grading: {
				stepper: "Grading",
				title: "What makes a lead Prio 1 for you?",
				desc: "Each criterion gives a lead +1, +2 or +3 points — or disqualifies it; the points add up to the Prio grade. You can score by firmographics and/or through AI agents — go through the criteria one at a time and switch off anything that shouldn't matter.",
				railNote:
					"Now the <b>grading</b>. Every active criterion scores a lead <b>+1 to +3 points</b> — or <b>disqualifies</b> it — and the total becomes Prio 1, 2 or 3. You can grade by <b>firmographics</b> and/or through <b>AI agents</b>. Industries start <b>switched off</b>; turn them on if they matter. The <b>Strategic Fit</b> agent judges what hard numbers can't — described in your own words.",
			},
			ceo: {
				stepper: "CEO scoring",
				title: "Extra scoring for CEOs?",
				// PREC-3896: the screen opened with a four-line grey box before the first thing worth
				// looking at. Everything it said that a user has to know is here, in the one line
				// under the header — scope included, because "are CEOs important to you?" is the
				// misreading PREC-3904 was fixing.
				desc: "A CEO is your best lead at a small company and out of reach at a large one. This decides which rules CEOs, MDs and founders are scored by — nobody else is affected.",
				railNote:
					"Quick sanity check: a <b>CEO at a 20-person startup</b> is often your best lead. A <b>CEO at a 12,000-person enterprise</b> usually isn't — your real buyer there is a department lead. Say yes and we run a <b>second scoring just for CEOs & MDs</b>: above your company-size cutoff they're disqualified, below it they score normally. Everyone else is untouched.",
				gateHint: "Pick yes or no to continue.",
			},
		},
	},
	scoringActivate: {
		railLabel: "Check & activate",
		title: "Does this look right?",
		desc: "Your real leads, scored with everything you just set. Hover a grade to see why — click it to correct it.",
		railNote:
			"The fun part 🎉 Real leads, scored with your setup. If a grade is wrong, <b>click it and correct it</b> — when you continue, we analyze your corrections, suggest scoring updates for you to review, and <b>re-test</b> before going live.",
		screens: {
			demo: {
				stepper: "How feedback works",
				title: "You can correct us — here's how",
				desc: "Watch once — then try it on your real leads in the next step.",
				railNote:
					"One last thing before your leads: the scoring is <b>correctable</b>. <b>Hover</b> any grade to see exactly why, <b>click</b> it to change it. When you change something, we <b>analyze your corrections</b> and suggest scoring updates — which you accept or reject, one by one. You're always in control.",
			},
			check: {
				stepper: "Check & activate",
				title: "Does this look right?",
				desc: "Your real leads, scored with everything you just set. Hover a grade to see why — click it to correct it.",
				railNote:
					"The fun part 🎉 Real leads, scored with your setup. If a grade is wrong, <b>click it and correct it</b> — when you continue, we analyze your corrections, suggest scoring updates for you to review, and <b>re-test</b> before going live.",
			},
		},
	},
};
